import { Middleware, Context } from '@nuxt/types';
import { getInstance } from '../plugins/authWrapper';

const loginWithRedirect = (authService, context: Context): void => {
  // If the user visits the app with a 'screen_hint' query param, we want
  // to pass this param to Auth0. We check if it belongs to hints set
  // The 'screen_hint' key passed to `loginWithRedirect` is read on our
  // custom Auth0 login page.
  const hints = ['login', 'signup', 'reset'];
  const screenHint = context.route.query?.screen_hint as string;
  const screen_hint = hints.indexOf(screenHint) >= 0 ? screenHint : 'login';

  authService.loginWithRedirect({ screen_hint });
};

function setRedirectTo(context: Context) {
  // save target route before login
  if (
    !context.store.getters['user-preference/onSignInRedirectTo'] &&
    context.route.fullPath != '/' &&
    !context.route.fullPath.startsWith('/?code')
  ) {
    context.store.commit(
      'user-preference/setOnSignInRedirect',
      {
        shouldRedirectOnSignIn: true,
        onSignInRedirectTo: context.route.fullPath
      },
      { root: true }
    );
    console.log(context.store.getters['user-preference/onSignInRedirectTo']);
  }
}

const auth: Middleware = async (context: Context): Promise<void> => {
  const authService = getInstance();

  // If the `authService` has finished loading (i.e. doing authentication checks)
  // and the user is authenticated, this middleware passes. If the `authService` has
  // finished, but the user is not authenticated, the user is redirected to Auth0 to
  // login.
  //
  // If the `authService` is still working, the middleware needs to return a promise
  // that resolves when the `authService` has finished loading and the user's auth
  // status has been checked. To achieve this, a watcher is added for the `authService`'s
  // `loading` property which will resolve the promise that is returned by the middleware
  // when `authService` has finished loading and the user is authenticated. If the user is
  // not authenticated, the user is redirected to Auth0 to login.

  if (!authService.loading) {
    if (authService.isAuthenticated) {
      return;
    } else {
      setRedirectTo(context);
      // The middleware needs to return an unresolved promise so that Nuxt will block. If
      // this promise is not returned and only `loginWithRedirect` is called, the middleware
      // will pass before the user has been redirected to Auth0 (i.e. before `loginWithRedirect`
      // has finished executing). This means Nuxt will continue to execute other middleware
      // and the user will possibly see the page component load before being redirected.
      return new Promise(() => {
        loginWithRedirect(authService, context);
      });
    }
  } else {
    setRedirectTo(context);

    let resolveAuthCheck;
    const authCheckPromise = new Promise(resolve => {
      resolveAuthCheck = resolve;
    });

    const unwatch = authService.$watch('loading', (loading: boolean) => {
      if (!loading) {
        if (authService.isAuthenticated) resolveAuthCheck();
        else loginWithRedirect(authService, context);
      }
    });

    await authCheckPromise;
    unwatch(); // remove watcher when the user's auth status has been checked
  }
};

export default auth;
